/**
 * Bike preference sections for the transports MiniModal (excludes personal-bike ownership).
 */

import React, { useCallback, useMemo } from "react";
import { bikePrefsSlice, rentalVehiclesPrefsSlice, usePersistedSlice } from "../../../../../store/persistence";
import { isRentalRoutingEnabled } from "../../../../../tools/rentalVehiclePrefs.tools";
import ImageOptionSelector, { type ImageSelectorOption } from "../../../../MiniModal/ImageOptionSelector";
import MiniModalSection from "../../../../MiniModal/MiniModalSection";

/** Supported bike-speed option ids (km/h) exposed in the selector. */
type BikeSpeedOptionId = "15" | "25" | "45";

/** Supported bike-effort option ids exposed in the selector. */
type BikeEffortOptionId = "minimum" | "balanced" | "maximum";

/** Union of the elevation-cost values persisted in the bike params. */
type ElevationCostsValue = "LOW" | "NONE" | "HIGH";

/** Speed options displayed in the MiniModal "Vitesse" section. */
const BIKE_SPEED_OPTIONS: readonly ImageSelectorOption[] = [
    {
        "id":       "15",
        "label":    "15 km/h",
        "imageSrc": "/assets/icon/bike_prefs/bike_speeds/15kmh.webp",
    },
    {
        "id":       "25",
        "label":    "25 km/h",
        "imageSrc": "/assets/icon/bike_prefs/bike_speeds/25kmh.webp",
    },
    {
        "id":       "45",
        "label":    "45 km/h",
        "imageSrc": "/assets/icon/bike_prefs/bike_speeds/45kmh.webp",
    },
];

/** Effort options displayed in the MiniModal "Effort" section. */
const BIKE_EFFORT_OPTIONS: readonly ImageSelectorOption[] = [
    {
        "id":       "minimum",
        "label":    "Minimum",
        "imageSrc": "/assets/icon/bike_prefs/bike_efforts/BikeMinEffort.webp",
        "imgCover": false,
    },
    {
        "id":       "balanced",
        "label":    "Equilibré",
        "imageSrc": "/assets/icon/bike_prefs/bike_efforts/BikeMiddleEffort.webp",
        "imgCover": false,
    },
    {
        "id":       "maximum",
        "label":    "Maximum",
        "imageSrc": "/assets/icon/bike_prefs/bike_efforts/BikeMaxEffort.webp",
        "imgCover": false,
    },
];

/** Additional transport / end-by-bike options. */
const BIKE_OPTIONS: readonly ImageSelectorOption[] = [
    {
        "id":       "isPersonalBike",
        "label":    "Vélo personnel",
        "imageSrc": "/assets/icon/bike_prefs/PersonalBike.webp",
        "imgCover": false,
    },
    {
        "id":       "transportPersonalBike",
        "label":    "Transport du vélo",
        "imageSrc": "/assets/icon/bike_prefs/TakeBike.webp",
        "imgCover": false,
    },
    {
        "id":       "endItineraryByBike",
        "label":    "Fin de trajet en vélo",
        "imageSrc": "/assets/icon/bike_prefs/LastKm.webp",
        "imgCover": false,
    },
];

/**
 * Type guard for `BikeSpeedOptionId`.
 *
 * @param value Candidate string value.
 * @returns `true` when `value` is a supported bike-speed option id.
 */
const isBikeSpeedOptionId = ( value: string ): value is BikeSpeedOptionId => {
    return value === "15" || value === "25" || value === "45";
};

/**
 * Convert a bike-speed option id to the numeric speed stored in the bike params.
 *
 * @param optionId Selected option id.
 * @returns The matching speed in km/h.
 */
const getBikeSpeedFromOptionId = ( optionId: BikeSpeedOptionId ): 15 | 25 | 45 => {
    if ( optionId === "15" ) {
        return 15;
    }
    if ( optionId === "45" ) {
        return 45;
    }

    return 25;
};

/**
 * Type guard for `BikeEffortOptionId`.
 *
 * @param value Candidate string value.
 * @returns `true` when `value` is a supported effort option id.
 */
const isBikeEffortOptionId = ( value: string ): value is BikeEffortOptionId => {
    return value === "minimum" || value === "balanced" || value === "maximum";
};

/**
 * Map an effort option id to its elevation-cost counterpart persisted in the bike params.
 *
 * @param optionId Selected effort option id.
 * @returns The elevation cost policy to apply.
 */
const getElevationCostsFromOptionId = ( optionId: BikeEffortOptionId ): ElevationCostsValue => {
    switch ( optionId ) {
        case "minimum":
            return "HIGH";

        case "balanced":
            return "LOW";

        case "maximum":
            return "NONE";
    }
};

/**
 * Reverse mapping of `getElevationCostsFromOptionId`. Used to preselect the effort option on modal open.
 *
 * @param elevationCosts Current elevation-cost policy.
 * @returns The matching effort option id.
 */
const getBikeEffortOptionId = ( elevationCosts: ElevationCostsValue ): BikeEffortOptionId => {
    switch ( elevationCosts ) {
        case "LOW":
            return "balanced";

        case "HIGH":
            return "minimum";

        case "NONE":
            return "maximum";
    }
};

/**
 * Narrow `unknown` to a known `ElevationCostsValue`.
 *
 * @param value Candidate value pulled from storage.
 * @returns `true` when `value` is a recognised elevation-cost value.
 */
const isElevationCostsValue = ( value: unknown ): value is ElevationCostsValue => {
    return value === "LOW" || value === "NONE" || value === "HIGH";
};

/**
 * Fallback-safe coercion to `ElevationCostsValue`, defaulting to `NONE`.
 *
 * @param value Candidate value pulled from storage.
 * @returns The coerced elevation-cost policy.
 */
const normalizeElevationCostsValue = ( value: unknown ): ElevationCostsValue => {
    if ( isElevationCostsValue( value ) ) {
        return value;
    }

    return "NONE";
};

/**
 * Normalisation matrix for the transport / end-by-bike options.
 *
 * @param isPersonalBike Whether the user owns a personal bike.
 * @param hasRentalTypes Whether at least one shared vehicle type is enabled.
 * @param transportPersonalBike User-intent for transport.
 * @param endItineraryByBike User-intent for end-by-bike intent.
 * @returns Normalised transport / end-by-bike flags.
 */
const normalizeBikeOptionMatrix = (
    isPersonalBike: boolean,
    hasRentalTypes: boolean,
    transportPersonalBike: boolean,
    endItineraryByBike: boolean,
): {
    "transportPersonalBike": boolean;
    "endItineraryByBike":    boolean;
} => {
    const normalizedTransportPersonalBike = isPersonalBike === true && transportPersonalBike === true;
    const normalizedEndItineraryByBike = hasRentalTypes === true && endItineraryByBike === true;

    if ( hasRentalTypes === false ) {
        return {
            "transportPersonalBike": normalizedTransportPersonalBike,
            "endItineraryByBike":    false,
        };
    }
    if ( isPersonalBike === true && normalizedTransportPersonalBike === true ) {
        return {
            "transportPersonalBike": true,
            "endItineraryByBike":    true,
        };
    }
    if ( isPersonalBike === false && transportPersonalBike === true ) {
        return {
            "transportPersonalBike": false,
            "endItineraryByBike":    false,
        };
    }

    return {
        "transportPersonalBike": normalizedTransportPersonalBike,
        "endItineraryByBike":    normalizedEndItineraryByBike,
    };
};

interface BikePrefsModalContentProps {

    /** When `true`, use the dark palette variants. */
    "isDarkUi": boolean;
}

/**
 * Bike preference sections (speed, effort, options) for the transports modal.
 *
 * @param props Component props. See `BikePrefsModalContentProps`.
 * @returns Bike MiniModal sections excluding ownership.
 */
const BikePrefsModalContent: React.FC<BikePrefsModalContentProps> = ( { isDarkUi } ) => {
    const [ bikePrefs, setBikePrefs ] = usePersistedSlice( bikePrefsSlice );
    const [ rentalVehiclesPrefs ] = usePersistedSlice( rentalVehiclesPrefsSlice );
    const hasRentalTypes = isRentalRoutingEnabled( rentalVehiclesPrefs.enabledTypes );

    const currentBikeSpeedKmh = useMemo( (): 15 | 25 | 45 => {
        if ( bikePrefs.bikeSpeedKmh === 15 ) {
            return 15;
        }
        if ( bikePrefs.bikeSpeedKmh === 45 ) {
            return 45;
        }

        return 25;
    }, [ bikePrefs.bikeSpeedKmh ] );
    const effectiveBikeSpeedKmh = bikePrefs.isPersonalBike ? currentBikeSpeedKmh : 25;

    const bikeEffortSelection = useMemo<BikeEffortOptionId>( () => {
        const safeElevationCosts = normalizeElevationCostsValue( bikePrefs.elevationCosts as unknown );

        return getBikeEffortOptionId( safeElevationCosts );
    }, [ bikePrefs.elevationCosts ] );

    const bikeEffortSelectorOptions = useMemo<readonly ImageSelectorOption[]>( () => {
        const isEffortDisabled = bikePrefs.isPersonalBike === false && hasRentalTypes === false;

        return BIKE_EFFORT_OPTIONS.map( ( option ) => ( {
            ...option,
            "disabled":        isEffortDisabled,
            "disabledTooltip": isEffortDisabled
                ? "Activez un vélo personnel ou au moins un type de véhicule partagé."
                : undefined,
        } ) );
    }, [ bikePrefs.isPersonalBike, hasRentalTypes ] );

    const bikeSpeedSelectorOptions = useMemo<readonly ImageSelectorOption[]>( () => {
        return BIKE_SPEED_OPTIONS.map( ( option ) => ( {
            ...option,
            "disabled":        bikePrefs.isPersonalBike === false,
            "disabledTooltip": bikePrefs.isPersonalBike === false ? "Disponible uniquement avec un vélo personnel." : undefined,
        } ) );
    }, [ bikePrefs.isPersonalBike ] );

    const bikeOptionSelectorOptions = useMemo<readonly ImageSelectorOption[]>( () => {
        const isTransportDisabled = bikePrefs.isPersonalBike === false;
        const isTripEndLockedByTransport = bikePrefs.isPersonalBike === true && bikePrefs.transportPersonalBike === true;
        const isTripEndDisabled = hasRentalTypes === false || isTripEndLockedByTransport;

        return BIKE_OPTIONS.map( ( option ) => {
            if ( option.id === "isPersonalBike" ) {
                return option;
            }
            if ( option.id === "transportPersonalBike" ) {
                return {
                    ...option,
                    "disabled":        isTransportDisabled,
                    "disabledTooltip": isTransportDisabled ? "Disponible uniquement avec un vélo personnel." : undefined,
                };
            }

            return {
                ...option,
                "disabled":        isTripEndDisabled,
                "disabledTooltip": hasRentalTypes === false
                    ? "Disponible uniquement avec au moins un type de véhicule partagé activé."
                    : isTripEndLockedByTransport
                        ? "Activé automatiquement avec le transport du vélo."
                        : undefined,
            };
        } );
    }, [ bikePrefs.isPersonalBike, bikePrefs.transportPersonalBike, hasRentalTypes ] );

    const bikeOptionSelection = useMemo<readonly string[]>( () => {
        const selectedOptions: string[] = [];
        if ( bikePrefs.isPersonalBike === true ) {
            selectedOptions.push( "isPersonalBike" );
        }
        if ( bikePrefs.transportPersonalBike === true ) {
            selectedOptions.push( "transportPersonalBike" );
        }
        if ( bikePrefs.endItineraryByBike === true ) {
            selectedOptions.push( "endItineraryByBike" );
        }

        return selectedOptions;
    }, [ bikePrefs.isPersonalBike, bikePrefs.transportPersonalBike, bikePrefs.endItineraryByBike ] );

    /**
     * Apply a new traveling speed. Ignores the change when not in personal-bike mode.
     *
     * @param nextValue Newly selected radio value.
     */
    const handleBikeSpeedChange = useCallback( ( nextValue: string ): void => {
        if ( bikePrefs.isPersonalBike === false || !isBikeSpeedOptionId( nextValue ) ) {
            return;
        }

        setBikePrefs( {
            ...bikePrefs,
            "bikeSpeedKmh": getBikeSpeedFromOptionId( nextValue ),
        } );
    }, [ bikePrefs, setBikePrefs ] );

    /**
     * Apply updated transport / end-by-bike options with normalisation.
     *
     * @param nextValue Selected checkbox values.
     */
    const handleBikeOptionChange = useCallback( ( nextValue: readonly string[] ): void => {
        const selectedOptions = new Set( nextValue );
        const isPersonalBikeSelected = selectedOptions.has( "isPersonalBike" );
        const isTransportSelected = selectedOptions.has( "transportPersonalBike" );
        const isEndItineraryByBikeSelected = selectedOptions.has( "endItineraryByBike" );
        const tripEndWithTransportOff = bikePrefs.transportPersonalBike === true && isTransportSelected === false
            ? false
            : isEndItineraryByBikeSelected;
        const normalizedOptions = normalizeBikeOptionMatrix(
            isPersonalBikeSelected,
            hasRentalTypes,
            isTransportSelected,
            tripEndWithTransportOff,
        );

        setBikePrefs( {
            ...bikePrefs,
            "isPersonalBike":        isPersonalBikeSelected,
            "bikeSpeedKmh":          isPersonalBikeSelected ? currentBikeSpeedKmh : 25,
            "transportPersonalBike": normalizedOptions.transportPersonalBike,
            "endItineraryByBike":    normalizedOptions.endItineraryByBike,
        } );
    }, [ bikePrefs, currentBikeSpeedKmh, hasRentalTypes, setBikePrefs ] );

    /**
     * Apply a new effort policy by mapping it to elevation costs.
     *
     * @param nextValue Newly selected radio value.
     */
    const handleBikeEffortChange = useCallback( ( nextValue: string ): void => {
        if ( !isBikeEffortOptionId( nextValue ) ) {
            return;
        }

        setBikePrefs( {
            ...bikePrefs,
            "elevationCosts": getElevationCostsFromOptionId( nextValue ),
        } );
    }, [ bikePrefs, setBikePrefs ] );

    return (
        <>
            <MiniModalSection
                isDarkUi={isDarkUi}
                title="Options"
            >
                <ImageOptionSelector
                    columns={3}
                    isDarkUi={isDarkUi}
                    onChange={handleBikeOptionChange}
                    options={bikeOptionSelectorOptions}
                    selectionType="checkbox"
                    value={bikeOptionSelection}
                />
            </MiniModalSection>
            <MiniModalSection
                isDarkUi={isDarkUi}
                title="Vitesse"
            >
                <ImageOptionSelector
                    isDarkUi={isDarkUi}
                    onChange={handleBikeSpeedChange}
                    options={bikeSpeedSelectorOptions}
                    selectionType="radio"
                    value={String( effectiveBikeSpeedKmh )}
                />
            </MiniModalSection>
            <MiniModalSection
                isDarkUi={isDarkUi}
                title="Effort"
            >
                <ImageOptionSelector
                    isDarkUi={isDarkUi}
                    onChange={handleBikeEffortChange}
                    options={bikeEffortSelectorOptions}
                    selectionType="radio"
                    value={bikeEffortSelection}
                />
            </MiniModalSection>
        </>
    );
};

export default BikePrefsModalContent;
